import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface TierPayload {
  name: string;
  description?: string;
  amount: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  benefits: string[];
  isPublic: boolean;
  maxPatrons?: number;
  DiscordRoleIds?: string[];
  customButtonText?: string;
  accessUrl?: string;
  sortOrder?: number;
  freeTrialDays?: number;
  isPerCreation?: boolean;
}

export const creatorService = {
  async getMyCreatorProfile() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/creators/profile/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async getPublicCreator(slugOrId: string) {
    const res = await axios.get(`${API_BASE_URL}/api/creators/${slugOrId}`);
    return res.data as { creator: any };
  },

  async getCreatorTiers(creatorId: string) {
    const res = await axios.get(`${API_BASE_URL}/api/creators/${creatorId}`);
    return (res.data.creator?.tiers || []) as any[];
  },

  async createCreatorProfile(form: any) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/creators/profile`, form, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.creatorProfile;
  },

  async updateCreatorProfile(form: any) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/creators/profile`, form, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.creatorProfile;
  },
  async getMyTiers() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/creators/tiers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.tiers as any[];
  },

  async createTier(payload: TierPayload) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/creators/tiers`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.tier;
  },

  async updateTier(id: string, payload: Partial<TierPayload>) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/creators/tiers/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.tier;
  },

  async deleteTier(id: string) {
    const token = localStorage.getItem('accessToken');
    await axios.delete(`${API_BASE_URL}/api/creators/tiers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  async searchCreators(params: {
    query: string;
    limit?: number;
    offset?: number;
    category?: string;
    minPatrons?: number;
    maxPatrons?: number;
    sort?: 'popular' | 'newest' | 'az';
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/creators/search`, {
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return res.data as {
      creators: any[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
      aggregations?: {
        patronCount?: Array<{ key: string; count: number }>;
        categories?: Array<{ key: string; count: number }>;
      };
    };
  },

  async getAnalytics(params: { period?: '7d' | '30d' | '90d' | '1y' } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/creators/analytics`, {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      period: string;
      analytics: Array<{
        id: string;
        date: string;
        totalPatrons: number;
        newPatrons: number;
        totalRevenue: number;
        monthlyRevenue: number;
        postsCount: number;
        commentsCount: number;
        likesCount: number;
      }>;
      current: {
        totalPatrons: number;
        monthlyRevenue: number;
        newPatronsInPeriod: number;
        postsInPeriod: number;
      };
      revenueBreakdown?: Array<{ tierName: string; revenue: number; patrons: number; percentage: number }>;
      topPosts?: Array<{ id: string; title: string; contentType: string; likes: number; comments: number; views: number; publishedAt: string }>;
    };
  },

  async exportAnalyticsCSV(params: { period?: '7d' | '30d' | '90d' | '1y' } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/creators/analytics/export`, {
      params,
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as Blob;
  },
};


